import { UserPreferences } from "./storage";

// POOCOO Lite - Main Script
// Clean, minimal implementation

// =============================================================================
// TYPES
// =============================================================================

interface SearchElements {
  input: HTMLInputElement;
  clearBtn: HTMLButtonElement | null;
  filterBtn: HTMLButtonElement | null;
  filterPanel: HTMLElement | null;
  resultsContainer: HTMLElement | null;
  checkResultContainer: HTMLElement | null;
  instructionPanel: HTMLElement | null;
  resetBtn: HTMLElement | null;
  dictSwitch: HTMLElement | null;
}

// Global translations object
declare global {
  interface Window {
    XT: {
      Js_Searching: string;
      Js_Error: string;
    };
    SiteConfig: {
      RouteTemplate: string;
    };
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// =============================================================================
// ANALYTICS
// =============================================================================

function trackEvent(eventName: string, params?: Record<string, any>): void {
  if (window.gtag) {
    window.gtag("event", eventName, params);
  } else if (window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...params });
  }
}

// =============================================================================
// UTILITIES
// =============================================================================

function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  ms: number,
): T {
  let timeout: ReturnType<typeof setTimeout>;
  return ((...args: unknown[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), ms);
  }) as T;
}

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function getSelectedDictionaryId(): number {
  const switcher = $("dict-switch");
  if (!switcher) return 0;
  const checked = switcher.querySelector<HTMLInputElement>(
    'input[name="dictionary"]:checked',
  );
  return checked ? parseInt(checked.value, 10) : 0;
}

// =============================================================================
// EDITING STATE
// =============================================================================

function updateEditingState(els: SearchElements): void {
  const { input, filterPanel, dictSwitch } = els;
  const active = document.activeElement;

  const inSearchUI =
    active === input ||
    active?.closest(".search") !== null ||
    active?.closest(".filters") !== null ||
    active?.closest(".dict-switch") !== null;

  const hasText = input.value.trim() !== "";
  const filtersOpen = filterPanel && !filterPanel.hidden;

  const filterInputs =
    document.querySelectorAll<HTMLInputElement>(".filters__input");
  const hasFilterValues = Array.from(filterInputs).some(
    (i) => i.value.trim() !== "",
  );

  const hasDictSelected = dictSwitch
    ? getSelectedDictionaryId() !== 0
    : false;

  const isEditing = inSearchUI || hasText || filtersOpen || hasFilterValues;

  document.body.classList.toggle("is-editing", isEditing);
}

// =============================================================================
// SEARCH API
// =============================================================================

function toggleInstruction(els: SearchElements, show: boolean): void {
  if (els.instructionPanel) {
    if (show && els.instructionPanel.innerHTML.trim() === "") {
      const template = document.getElementById(
        "instruction-template",
      ) as HTMLTemplateElement;
      if (template) {
        els.instructionPanel.appendChild(template.content.cloneNode(true));
      }
    }
    els.instructionPanel.style.display = show ? "block" : "none";
  }
}

async function checkWord(
  letters: string,
  container: HTMLElement | null,
): Promise<void> {
  if (!container) return;

  if (!letters || letters.trim().length < 2) {
    container.innerHTML = "";
    return;
  }

  try {
    const res = await fetch("/Search/SearchAction", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        Letters: letters,
        Action: 1,
        SelectedDictionaryId: getSelectedDictionaryId(),
      }),
    });

    if (res.ok) {
      container.innerHTML = await res.text();
    }
  } catch (err) {
    console.error("Check word error:", err);
  }
}

async function searchWords(
  letters: string,
  els: SearchElements,
): Promise<void> {
  const { resultsContainer, checkResultContainer } = els;
  if (!resultsContainer) return;

  const filters = getFilterValues();
  const hasFilters = !!(
    filters.length ||
    filters.startsWith ||
    filters.contains ||
    filters.endsWith
  );
  const hasLetters = letters && letters.trim().length >= 2;

  // No letters and no filters - reset to homepage
  if (!hasLetters && !hasFilters) {
    resultsContainer.innerHTML = "";
    if (checkResultContainer) checkResultContainer.innerHTML = "";
    toggleInstruction(els, true);
    updateUrl({});
    return;
  }

  resultsContainer.classList.add("is-loading");
  toggleInstruction(els, false);

  trackEvent("search_words", {
    search_term: letters,
    has_filters: hasFilters,
  });

  const searchPromise = fetch("/Search/SearchAction", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      Letters: letters,
      Action: 0,
      SelectedDictionaryId: getSelectedDictionaryId(),
      FilteringEnabled: hasFilters,
      FilteringLength: filters.length ? parseInt(filters.length) : null,
      FilteringStartsWith: filters.startsWith || null,
      FilteringEndsWith: filters.endsWith || null,
      FilteringContains: filters.contains || null,
    }),
  });

  const checkPromise = checkWord(letters, checkResultContainer);

  try {
    const [searchRes] = await Promise.all([searchPromise, checkPromise]);

    if (!searchRes.ok) throw new Error("Search failed");
    resultsContainer.innerHTML = await searchRes.text();

    // Update URL with current state
    updateUrl({
      letters: hasLetters ? letters : undefined,
      ...filters,
    });
  } catch (err) {
    console.error("Search error:", err);
    resultsContainer.innerHTML = `<div class="no-results">${window.XT.Js_Error}</div>`;
  } finally {
    resultsContainer.classList.remove("is-loading");
  }
}

// =============================================================================
// URL MANAGEMENT
// =============================================================================

interface UrlParams {
  letters?: string;
  length?: string;
  startsWith?: string;
  contains?: string;
  endsWith?: string;
}

function buildUrl(params: UrlParams): string {
  const { letters, length, startsWith, contains, endsWith } = params;

  const queryParams = new URLSearchParams();
  if (length) queryParams.set("length", length);
  if (startsWith) queryParams.set("startsWith", startsWith);
  if (contains) queryParams.set("contains", contains);
  if (endsWith) queryParams.set("endsWith", endsWith);
  const queryString = queryParams.toString();

  if (letters && window.SiteConfig?.RouteTemplate) {
    // With letters: /unscramble/cars or /unscramble/cars?length=4
    const urlLetters = letters.toLowerCase().replace(/[?.*#]/g, "_");
    let url =
      "/" + window.SiteConfig.RouteTemplate.replace("{letters}", urlLetters);
    if (queryString) url += `?${queryString}`;
    return url;
  } else if (queryString) {
    // Only filters: /?length=4
    return `/?${queryString}`;
  } else {
    // Homepage
    return "/";
  }
}

function updateUrl(params: UrlParams): void {
  const newUrl = buildUrl(params);
  const currentUrl = window.location.pathname + window.location.search;

  if (currentUrl !== newUrl) {
    window.history.pushState(params, "", newUrl);
    trackEvent("virtual_page_view", { page_path: newUrl });
  }
}

function getFilterValues(): Omit<UrlParams, "letters"> {
  return {
    length: ($("filter-length") as HTMLInputElement)?.value || undefined,
    startsWith: ($("filter-starts") as HTMLInputElement)?.value || undefined,
    contains: ($("filter-contains") as HTMLInputElement)?.value || undefined,
    endsWith: ($("filter-ends") as HTMLInputElement)?.value || undefined,
  };
}

function parseStateFromUrl(): UrlParams {
  const urlParams = new URLSearchParams(window.location.search);
  const path = window.location.pathname;

  let letters: string | undefined;

  // Parse letters from URL path using RouteTemplate
  if (window.SiteConfig?.RouteTemplate) {
    const template = window.SiteConfig.RouteTemplate;
    const placeholderIndex = template.indexOf("{letters}");

    if (placeholderIndex !== -1) {
      const prefix = "/" + template.substring(0, placeholderIndex);
      if (path.startsWith(prefix) && path.length > prefix.length) {
        letters = path.substring(prefix.length).replace(/_/g, "?");
      }
    }
  }

  return {
    letters,
    length: urlParams.get("length") || undefined,
    startsWith: urlParams.get("startsWith") || undefined,
    contains: urlParams.get("contains") || undefined,
    endsWith: urlParams.get("endsWith") || undefined,
  };
}

function applyState(state: UrlParams, els: SearchElements): void {
  const { letters, length, startsWith, contains, endsWith } = state;
  const hasFilters = !!(length || startsWith || contains || endsWith);

  // Update main input
  els.input.value = letters || "";
  if (els.clearBtn) els.clearBtn.hidden = !letters;

  // Update filter inputs
  const lengthInput = $("filter-length") as HTMLInputElement;
  const startsInput = $("filter-starts") as HTMLInputElement;
  const containsInput = $("filter-contains") as HTMLInputElement;
  const endsInput = $("filter-ends") as HTMLInputElement;

  if (lengthInput) lengthInput.value = length || "";
  if (startsInput) startsInput.value = startsWith || "";
  if (containsInput) containsInput.value = contains || "";
  if (endsInput) endsInput.value = endsWith || "";

  // Update filter clear buttons
  document.querySelectorAll<HTMLElement>(".filters__field").forEach((field) => {
    const input = field.querySelector<HTMLInputElement>(".filters__input");
    const clearBtn = field.querySelector<HTMLButtonElement>(".filters__clear");
    if (input && clearBtn) clearBtn.hidden = !input.value;
  });

  // Show/hide filter panel
  if (els.filterPanel && els.filterBtn) {
    els.filterPanel.hidden = !hasFilters;
    els.filterBtn.classList.toggle("search__btn--active", hasFilters);
  }

  // Perform search or reset
  const hasLetters = letters && letters.trim().length >= 2;
  if (hasLetters || hasFilters) {
    searchWords(letters || "", els);
  } else {
    if (els.resultsContainer) els.resultsContainer.innerHTML = "";
    if (els.checkResultContainer) els.checkResultContainer.innerHTML = "";
    toggleInstruction(els, true);
  }

  updateEditingState(els);
}

function setupHistoryNavigation(els: SearchElements): void {
  window.addEventListener("popstate", () => {
    const state = parseStateFromUrl();
    applyState(state, els);
  });
}

function syncInputsWithUrl(): void {
  // Get filter values from URL query params
  const urlParams = new URLSearchParams(window.location.search);

  const urlLength = urlParams.get("length") || "";
  const urlStartsWith = urlParams.get("startsWith") || "";
  const urlContains = urlParams.get("contains") || "";
  const urlEndsWith = urlParams.get("endsWith") || "";

  // Sync filter inputs with URL (override browser-restored values)
  const lengthInput = $("filter-length") as HTMLInputElement;
  const startsInput = $("filter-starts") as HTMLInputElement;
  const containsInput = $("filter-contains") as HTMLInputElement;
  const endsInput = $("filter-ends") as HTMLInputElement;

  if (lengthInput) lengthInput.value = urlLength;
  if (startsInput) startsInput.value = urlStartsWith;
  if (containsInput) containsInput.value = urlContains;
  if (endsInput) endsInput.value = urlEndsWith;
}

// =============================================================================
// CLEAR
// =============================================================================

function clearAll(els: SearchElements): void {
  els.input.value = "";
  if (els.clearBtn) els.clearBtn.hidden = true;
  if (els.checkResultContainer) els.checkResultContainer.innerHTML = "";

  // Check if filters are still active
  const filters = getFilterValues();
  const hasFilters = !!(
    filters.length ||
    filters.startsWith ||
    filters.contains ||
    filters.endsWith
  );

  if (hasFilters) {
    // Filters active - search with filters only
    searchWords("", els);
  } else {
    // No filters - show instruction and reset
    if (els.resultsContainer) els.resultsContainer.innerHTML = "";
    toggleInstruction(els, true);
    updateUrl({});
  }
}

function resetAll(els: SearchElements): void {
  // Clear main input
  els.input.value = "";
  if (els.clearBtn) els.clearBtn.hidden = true;

  // Clear all filters
  const filterInputs =
    document.querySelectorAll<HTMLInputElement>(".filters__input");
  filterInputs.forEach((input) => (input.value = ""));

  const filterClears =
    document.querySelectorAll<HTMLElement>(".filters__clear");
  filterClears.forEach((btn) => (btn.hidden = true));

  // Close filter panel
  if (els.filterPanel) {
    els.filterPanel.hidden = true;
    if (els.filterBtn) els.filterBtn.classList.remove("search__btn--active");
  }

  // Clear results and show instruction
  if (els.resultsContainer) els.resultsContainer.innerHTML = "";
  if (els.checkResultContainer) els.checkResultContainer.innerHTML = "";
  toggleInstruction(els, true);

  // Reset URL and state
  updateUrl({});
  updateEditingState(els);

  // Focus input
  els.input.focus();
}

// =============================================================================
// EVENT HANDLERS
// =============================================================================

function setupSearch(): void {
  const input = $("search-input") as HTMLInputElement;
  if (!input) return;

  // Sync inputs with URL to prevent browser-restored values from causing issues
  syncInputsWithUrl();

  const els: SearchElements = {
    input,
    clearBtn: $("search-clear") as HTMLButtonElement,
    filterBtn: $("search-filter-toggle") as HTMLButtonElement,
    filterPanel: $("filter-panel"),
    resultsContainer: $("search-results"),
    checkResultContainer: $("check-word-result"),
    instructionPanel: $("instruction-panel"),
    resetBtn: $("search-reset"),
    dictSwitch: $("dict-switch"),
  };

  // Restore dictionary selection from preferences
  let dictRestored = false;
  if (els.dictSwitch) {
    const storedDictId = UserPreferences.getDictionary();
    if (storedDictId !== 0) {
      const radio = els.dictSwitch.querySelector<HTMLInputElement>(
        `input[name="dictionary"][value="${storedDictId}"]`,
      );
      if (radio && !radio.checked) {
        radio.checked = true;
        dictRestored = true;
      }
    }
  }

  const debouncedSearch = debounce(() => searchWords(input.value, els), 300);

  // Input events
  input.addEventListener("focus", () => {
    if (!document.body.classList.contains("is-editing")) {
      window.scrollTo(0, 0);
    }
    updateEditingState(els);
  });
  input.addEventListener("blur", () => {
    requestAnimationFrame(() => updateEditingState(els));
  });
  input.addEventListener("input", () => {
    if (els.clearBtn) els.clearBtn.hidden = input.value.length === 0;
    updateEditingState(els);
    debouncedSearch();
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      searchWords(input.value, els);
    }
  });

  // Clear button
  if (els.clearBtn) {
    els.clearBtn.addEventListener("mousedown", (e) => e.preventDefault());
    els.clearBtn.addEventListener("click", () => clearAll(els));
  }

  // Reset button (logo icon)
  if (els.resetBtn) {
    els.resetBtn.addEventListener("click", () => resetAll(els));
  }

  // Dictionary switch
  if (els.dictSwitch) {
    els.dictSwitch.addEventListener("mousedown", (e) => e.preventDefault());

    const radios = els.dictSwitch.querySelectorAll<HTMLInputElement>(
      'input[name="dictionary"]',
    );
    radios.forEach((radio) => {
      radio.addEventListener("change", () => {
        trackEvent("dictionary_change", { dictionary_id: radio.value });
        UserPreferences.setDictionary(parseInt(radio.value, 10));
        input.focus();
        updateEditingState(els);
        debouncedSearch();
      });
    });
  }

  // Filter toggle
  if (els.filterBtn && els.filterPanel) {
    els.filterBtn.addEventListener("mousedown", (e) => e.preventDefault());
    els.filterBtn.addEventListener("click", () => {
      const isClosing = !els.filterPanel!.hidden;
      els.filterPanel!.hidden = !els.filterPanel!.hidden;
      els.filterBtn!.classList.toggle(
        "search__btn--active",
        !els.filterPanel!.hidden,
      );

      trackEvent("toggle_filters", { state: isClosing ? "close" : "open" });

      if (isClosing) {
        const filterInputs =
          document.querySelectorAll<HTMLInputElement>(".filters__input");
        filterInputs.forEach((input) => (input.value = ""));

        const filterClears =
          document.querySelectorAll<HTMLElement>(".filters__clear");
        filterClears.forEach((btn) => (btn.hidden = true));

        els.input.focus();
        debouncedSearch();
      }

      updateEditingState(els);
    });
  }

  // Filter inputs
  const filterFields =
    document.querySelectorAll<HTMLElement>(".filters__field");
  filterFields.forEach((field) => {
    const input = field.querySelector<HTMLInputElement>(".filters__input");
    const clearBtn = field.querySelector<HTMLButtonElement>(".filters__clear");

    if (!input) return;

    const updateClearBtn = () => {
      if (clearBtn) clearBtn.hidden = input.value.length === 0;
    };

    input.addEventListener("input", () => {
      // Validate max value for number inputs
      if (input.type === "number" && input.value) {
        if (input.max) {
          const max = parseInt(input.max, 10);
          if (parseInt(input.value, 10) > max) {
            input.value = max.toString();
          }
        }
        if (input.min) {
          // Optional: You might not want to auto-correct min immediately while typing
          // because the user might be typing a multi-digit number (e.g. typing "10" starts with "1")
          // But if needed, logic would go here. For now, we trust standard browser validation or check on blur.
        }
      }

      updateClearBtn();
      debouncedSearch();
    });
    input.addEventListener("focus", () => updateEditingState(els));
    input.addEventListener("blur", () => {
      requestAnimationFrame(() => updateEditingState(els));
    });

    if (clearBtn) {
      clearBtn.addEventListener("mousedown", (e) => e.preventDefault());
      clearBtn.addEventListener("click", () => {
        input.value = "";
        updateClearBtn();
        input.focus();
        debouncedSearch();
      });
    }

    updateClearBtn();
  });

  // Init state
  updateEditingState(els);
  if (els.clearBtn) els.clearBtn.hidden = input.value.length === 0;

  // Check for browser-restored filter values
  const filters = getFilterValues();
  const hasRestoredFilters = !!(
    filters.length ||
    filters.startsWith ||
    filters.contains ||
    filters.endsWith
  );

  // If browser restored filter values, show filter panel and update clear buttons
  if (hasRestoredFilters && els.filterPanel) {
    els.filterPanel.hidden = false;
    if (els.filterBtn) els.filterBtn.classList.add("search__btn--active");

    // Update clear buttons for restored values
    filterFields.forEach((field) => {
      const fieldInput =
        field.querySelector<HTMLInputElement>(".filters__input");
      const clearBtn =
        field.querySelector<HTMLButtonElement>(".filters__clear");
      if (fieldInput && clearBtn) {
        clearBtn.hidden = fieldInput.value.length === 0;
      }
    });
  }

  // Auto-search if input or filters have values AND results container is empty (not server-rendered)
  // OR if we restored a different dictionary than the default/server-rendered one.
  const hasValues = input.value.trim() || hasRestoredFilters;
  const resultsEmpty =
    els.resultsContainer && els.resultsContainer.innerHTML.trim() === "";

  if (hasValues && (resultsEmpty || dictRestored)) {
    searchWords(input.value, els);
  }

  // Setup browser back/forward navigation
  setupHistoryNavigation(els);

  // Remove no-transitions class after a short delay to allow server-side state to settle
  setTimeout(() => {
    document.body.classList.remove("no-transitions");
  }, 100);
}

function setupKeyboardShortcuts(): void {
  const input = $("search-input") as HTMLInputElement;
  if (!input) return;

  document.addEventListener("keydown", (e) => {
    const active = document.activeElement as HTMLElement;

    // "/" to focus
    if (e.key === "/" && active?.tagName !== "INPUT") {
      e.preventDefault();
      input.focus();
      input.select();
    }

    // Escape to clear/blur
    if (e.key === "Escape") {
      if (input.value) {
        input.value = "";
        input.dispatchEvent(new Event("input"));
      } else {
        input.blur();
      }
    }
  });
}

function registerServiceWorker(): void {
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/service-worker.js")
        .catch(console.error);
    });
  }
}

// =============================================================================
// THEME TOGGLE
// =============================================================================

function applyTheme(theme: "dark" | "light"): void {
  const root = document.documentElement;
  if (theme === "light") {
    root.classList.add("theme-light");
  } else {
    root.classList.remove("theme-light");
  }
}

function getCurrentTheme(): "dark" | "light" {
  return document.documentElement.classList.contains("theme-light")
    ? "light"
    : "dark";
}

function setupThemeToggle(): void {
  const toggleBtn = $("theme-toggle");
  if (!toggleBtn) return;

  toggleBtn.addEventListener("click", () => {
    const currentTheme = getCurrentTheme();
    const newTheme = currentTheme === "dark" ? "light" : "dark";
    applyTheme(newTheme);
    UserPreferences.setTheme(newTheme);
    trackEvent("theme_toggle", { theme: newTheme });
  });
}

function initTheme(): void {
  // Apply stored theme immediately (before DOM ready for instant apply)
  const storedTheme = UserPreferences.getTheme();
  if (storedTheme) {
    applyTheme(storedTheme);
  }
  // Default is dark (no class needed)
}

// =============================================================================
// INIT
// =============================================================================

import { initWordMenuModal } from "./word-menu-modal";

export function init(): void {
  initTheme();
  registerServiceWorker();
  setupKeyboardShortcuts();
  setupSearch();
  setupThemeToggle();
  initWordMenuModal();
}
